import React from 'react';
import { 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText, 
  alpha 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { getBackgroundColor } from '../themes';

// Icons
import FolderMoveIcon from '@mui/icons-material/DriveFileMoveOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutline';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: theme.shape.borderRadius,
    minWidth: 180,
    boxShadow: theme.shadows[3],
    padding: theme.spacing(0.5, 0),
    backgroundColor: getBackgroundColor(theme, 'paper'),
    '& .MuiMenu-list': {
      padding: theme.spacing(0.5, 0),
    },
  }
}));

const StyledMenuItem = styled(MenuItem)(({ theme, danger }) => ({
  minHeight: 42,
  padding: theme.spacing(0.5, 2),
  margin: theme.spacing(0.25, 0.5),
  borderRadius: theme.shape.borderRadius / 2,
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: danger 
      ? alpha(theme.palette.error.main, 0.1)
      : theme.palette.action.hover
  },
  '& .MuiListItemIcon-root': {
    minWidth: 36,
    color: danger ? theme.palette.error.main : 'inherit'
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.85rem',
    fontWeight: danger ? 500 : 400,
    color: danger ? theme.palette.error.main : theme.palette.text.primary
  }
}));

const ChatSessionContextMenu = ({ 
  anchorEl, 
  onClose, 
  isInProject, 
  onRename, 
  onDelete, 
  onMoveToProjectClick, 
  onRemoveFromProject,
  onExport,
  disabled,
}) => {
  const { t } = useTranslation();

  return (
    <StyledMenu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      data-testid="chat-session-context-menu"
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <StyledMenuItem onClick={onRename} disabled={disabled} data-testid="rename-menu-item">
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('chatSession.rename')} />
      </StyledMenuItem>
      
      <StyledMenuItem onClick={onDelete} disabled={disabled} data-testid="delete-menu-item">
        <ListItemIcon>
          <DeleteIcon fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText primary={t('chatSession.delete.menu')} />
      </StyledMenuItem>
      
      {isInProject ? (
        <StyledMenuItem onClick={onRemoveFromProject} disabled={disabled} data-testid="remove-from-project-menu-item">
          <ListItemIcon>
            <RemoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('chatSession.removeFromProject')} />
        </StyledMenuItem>
      ) : (
        <StyledMenuItem onClick={onMoveToProjectClick} disabled={disabled} data-testid="move-to-project-menu-item">
          <ListItemIcon>
            <FolderMoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('chatSession.moveToProject')} />
        </StyledMenuItem>
      )}
      
      <StyledMenuItem onClick={onExport} disabled={disabled} data-testid="export-menu-item">
        <ListItemIcon>
          <DownloadIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('chatSession.export')} />
      </StyledMenuItem>
    </StyledMenu>
  );
};

export default ChatSessionContextMenu;